import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FRONT_URL,
  getUsersToAddInAssessment,
  updateAssessment,
  deleteUserFromAssessment,
  sendSelfLaunchEmails,
  sendSelfReminderEmails,
} from "../axios/axiosFunctions";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import { AssessmentContext } from "../contexts/assessments";
import { DeleteConfirmationModal } from "./common";
import { getColumnsHome } from "../utils/homeTable";

const UsersAssessment = ({ rows, assessmentId }) => {
  const navigate = useNavigate();
  const { refetchAssessments } = useContext(AssessmentContext);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [usersList, setUsersList] = useState([]);
  const [error, setError] = useState("");
  const [showError, setShowError] = useState(false);
  const [alertSeverity, setAlertSeverity] = useState("error");
  const [selectOpt, setSelectOpt] = useState([]);
  const [openDialogDelete, setOpenDialogDelete] = useState(false);
  const [userIdToDelete, setUserIdToDelete] = useState(null);
  const [localRows, setLocalRows] = useState(rows);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailDialogType, setEmailDialogType] = useState("launch");
  const [emailTargetUser, setEmailTargetUser] = useState(null);
  const [sendingEmails, setSendingEmails] = useState(false);
  const [emailResult, setEmailResult] = useState(null);

  const handleEdit = (userId) => {
    navigate(`/profile/${userId}`);
  };

  const handleDelete = (userId) => {
    setUserIdToDelete(userId);
    setOpenDialogDelete(true);
  };

  const handleCloseDialogDelete = () => {
    setOpenDialogDelete(false);
    setUserIdToDelete(null);
  };

  const confirmDelete = async () => {
    if (!userIdToDelete || !assessmentId) return;

    const res = await deleteUserFromAssessment(assessmentId, userIdToDelete);

    if (!res || res.success === false) {
      setError(res?.msg || "Error while deleting user, please try later");
      setAlertSeverity("error");
      setShowError(true);
      setTimeout(() => {
        setShowError(false);
      }, 4000);
      handleCloseDialogDelete();
      return;
    }

    setLocalRows((prevRows) => prevRows.filter((row) => row.id !== userIdToDelete));

    refetchAssessments();

    setError("User removed successfully");
    setAlertSeverity("success");
    setShowError(true);
    setTimeout(() => {
      setShowError(false);
    }, 2000);

    handleCloseDialogDelete();
  };

  const handleSendLaunchRow = (row) => {
    setEmailTargetUser(row);
    setEmailDialogType("launch");
    setEmailResult(null);
    setEmailDialogOpen(true);
  };

  const handleSendReminderRow = (row) => {
    setEmailTargetUser(row);
    setEmailDialogType("reminder");
    setEmailResult(null);
    setEmailDialogOpen(true);
  };

  const columns = getColumnsHome(
    handleEdit,
    handleDelete,
    handleSendLaunchRow,
    handleSendReminderRow,
  );

  useEffect(() => {
    if (assessmentId) {
      getUsersToAdd();
    }
  }, [assessmentId]);

  useEffect(() => {
    setLocalRows(rows);
  }, [rows]);

  const mapOptions = (usersArray) => {
    if (!usersArray || usersArray.length === 0) {
      setSelectOpt([]);
      return;
    }

    const tempOpt = usersArray.map((u, i) => ({
      label: `${u.firstName}${u.lastName ? ` ${u.lastName}` : ""}`,
      index: i,
    }));
    setSelectOpt(tempOpt);
  };

  const getUsersToAdd = async () => {
    const res = await getUsersToAddInAssessment(assessmentId);
    if (res) {
      setUsers(res.users);
      mapOptions(res.users);
    }
  };
  const handleCellClick = (e) => {
    const { row, colDef } = e;
    if (colDef.field === "lastName" || colDef.field === "firstName") {
      navigate(`/profile/${row.id}`);
    }
  };

  const handleDialogOpen = () => {
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    if (usersList.length === 0) {
      setDialogOpen(false);
      return;
    }
    setUsersList([]);
    getUsersToAdd();
    setDialogOpen(false);
  };

  const handleAddUsers = async () => {
    if (usersList.length === 0) {
      handleDialogClose();
      return;
    }

    const usersToAdd = usersList.map((u) => u._id);
    const currentUsers = localRows.map((u) => u.id);

    const data = {
      users: usersToAdd.concat(currentUsers),
    };

    const res = await updateAssessment(assessmentId, data);
    if (!res) {
      setError("Error while saving please try later");
      setShowError(true);
      setTimeout(() => {
        setShowError(false);
      }, 4000);
      return;
    }

    refetchAssessments();
    setError("Saved correctly");
    setAlertSeverity("success");
    setShowError(true);
    handleDialogClose();
    setTimeout(() => {
      setShowError(false);
    }, 2000);
  };

  const handleCloseEmailDialog = () => {
    if (sendingEmails) return;
    setEmailDialogOpen(false);
    setEmailTargetUser(null);
    setEmailResult(null);
  };

  const handleSendEmails = async () => {
    if (!assessmentId || !emailTargetUser) return;
    setSendingEmails(true);
    setEmailResult(null);
    const data = {
      frontUrl: FRONT_URL,
      userIds: [String(emailTargetUser.id)],
    };
    try {
      const fn = emailDialogType === "launch" ? sendSelfLaunchEmails : sendSelfReminderEmails;
      const res = await fn(assessmentId, data);
      if (res?.error) {
        setEmailResult({ severity: "error", msg: res.msg || "Failed to send email." });
      } else {
        const sent = res?.sent ?? 0;
        const failed = res?.failed ?? 0;
        const skipped = res?.skippedCompleted ?? 0;
        let msg;
        if (sent > 0) {
          msg = `${emailDialogType === "launch" ? "Launch" : "Reminder"} email sent.`;
        } else if (skipped > 0) {
          msg = "User has already completed the assessment — reminder skipped.";
        } else if (failed > 0) {
          msg = "Failed to send email.";
        } else {
          msg = "No email sent.";
        }
        setEmailResult({
          severity: failed > 0 ? "error" : sent > 0 ? "success" : "warning",
          msg,
        });
      }
    } catch (err) {
      console.error(err);
      setEmailResult({ severity: "error", msg: "Failed to send email." });
    } finally {
      setSendingEmails(false);
    }
  };

  const handleChange = (e, newValue) => {
    if (!newValue) return;
    const { index } = newValue;
    const newUsersList = [...usersList];
    const selectedElemet = users[index];

    const newUsersToAdd = [...users.slice(0, index), ...users.slice(index + 1)];

    newUsersList.push(selectedElemet);
    setUsers(newUsersToAdd);
    mapOptions(newUsersToAdd);
    setUsersList(newUsersList);
  };

  const emailTargetLabel = emailTargetUser
    ? `${emailTargetUser.firstName || ""}${emailTargetUser.lastName ? ` ${emailTargetUser.lastName}` : ""} (${emailTargetUser.email})`
    : "";

  return (
    <Box>
      <Typography variant="h4" fontWeight={500} color="secondary">
        Users
      </Typography>
      <DataGrid
        columns={columns}
        rows={localRows}
        onCellClick={handleCellClick}
        sx={{ marginTop: "30px", marginBottom: "30px" }}
        showToolbar
      />
      <Box display="flex" gap={2} flexWrap="wrap" sx={{ mb: 2 }}>
        <Button
          onClick={handleDialogOpen}
          variant="contained"
          sx={{ color: "white" }}
        >
          Add User
        </Button>
      </Box>
      <Dialog open={emailDialogOpen} onClose={handleCloseEmailDialog}>
        <DialogTitle sx={{ fontWeight: "bold" }}>
          {emailDialogType === "launch" ? "Send Launch Email" : "Send Reminder Email"}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1 }}>
            {emailDialogType === "launch" ? (
              <>Send launch email to <strong>{emailTargetLabel}</strong>.</>
            ) : (
              <>
                Send reminder to <strong>{emailTargetLabel}</strong>
                {" "}if the assessment is not yet completed.
              </>
            )}
          </Typography>
          {emailResult && (
            <Alert severity={emailResult.severity} sx={{ mt: 1 }}>
              {emailResult.msg}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: "16px" }}>
          <Button onClick={handleCloseEmailDialog} disabled={sendingEmails} color="inherit">
            Cancel
          </Button>
          {!emailResult ? (
            <Button
              onClick={handleSendEmails}
              variant="contained"
              color={emailDialogType === "launch" ? "primary" : "info"}
              disabled={sendingEmails}
              startIcon={
                sendingEmails ? (
                  <CircularProgress size={18} color="inherit" />
                ) : emailDialogType === "launch" ? (
                  <EmailIcon />
                ) : (
                  <NotificationsActiveIcon />
                )
              }
            >
              {sendingEmails ? "Sending..." : "Send"}
            </Button>
          ) : (
            <Button onClick={handleCloseEmailDialog} variant="contained">
              Done
            </Button>
          )}
        </DialogActions>
      </Dialog>
      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        maxWidth="md"
        fullWidth
      >
        <DialogContent>
          <Typography variant="h5" fontWeight={500} color="secondary">
            Add Users
          </Typography>
          <Autocomplete
            onChange={handleChange}
            options={selectOpt}
            renderInput={(params) => <TextField {...params} label="Add user" />}
          />
          <Divider />
          {usersList.length > 0 && (
            <>
              <Typography
                variant="subtitle1"
                fontWeight={500}
                color="secondary"
                sx={{ marginTop: "20px" }}
              >
                Users to Add
              </Typography>
              <List>
                {usersList.map((u, i) => (
                  <ListItem key={i}>
                    <ListItemIcon>
                      <PersonIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={`${u.firstName}${u.lastName ? ` ${u.lastName}` : ""}`}
                    />
                  </ListItem>
                ))}
              </List>
            </>
          )}
          {showError && (
            <Alert sx={{ marginTop: "20px" }} severity={alertSeverity}>
              {error}
            </Alert>
          )}
          <Box sx={{ marginTop: "20px" }}>
            <Button
              onClick={handleAddUsers}
              variant="contained"
              sx={{ color: "white" }}
            >
              ADD USERS
            </Button>
            <Button
              onClick={handleDialogClose}
              variant="contained"
              sx={{ color: "white", marginLeft: "20px" }}
            >
              CANCEL
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
      <DeleteConfirmationModal
        openDialog={openDialogDelete}
        closeDialog={handleCloseDialogDelete}
        title="Delete User"
        message="Are you sure you want to delete this user from this assessment?"
        onConfirm={confirmDelete}
      />
      {showError && (
        <Alert sx={{ marginTop: "20px" }} severity={alertSeverity}>
          {error}
        </Alert>
      )}
    </Box>
  );
};

export default UsersAssessment;
